import { AlertFactory } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import { WorkItem } from 'jobs/bookkeeping/BookkeepingProgressionInfo'
import BookkeepingProgressionStore from 'jobs/bookkeeping/BookkeepingProgressionStore'
import { isNil } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { required, validationContext } from 'validx'
import { ViewModelList } from '../../../../../infra/ViewModelList'

export default class BookkeepingCompleteCycleDialogViewModel extends ModalViewModel {
  private readonly _workItemVms

  constructor(
    private newAlert: AlertFactory,
    private jobId: Id,
    private bookkeepingProgressionStore: BookkeepingProgressionStore,
  ) {
    super()
    makeObservable(this)

    this._workItemVms = new ViewModelList({
      models: () =>
        this.bookkeepingProgressionInfo?.workItems.filter(
          (wi) => wi.remaining > 0,
        ) ?? [],
      create: (wi) => new CompletedWorkItemViewModel(wi),
    })
  }

  /**
   * Returns the bookkeeping progression info
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this.bookkeepingProgressionStore.bookkeepingProgressions.get(
      this.jobId,
    )
  }

  /**
   * View models for service items to complete in the current cycle
   */
  @computed
  get itemsToComplete() {
    return this._workItemVms.items.slice()
  }

  @computed
  get completedItems() {
    return this.itemsToComplete.filter((itc) => itc.completedItems > 0)
  }

  @action.bound
  activate() {
    this._workItemVms.activate()
  }

  @action.bound
  deactivate() {
    this._workItemVms.deactivate()
  }

  /**
   * Validates each work item
   * @returns
   */
  @action.bound
  validate() {
    return this.itemsToComplete.every(
      (ci) => ci.validation.reset().validate().isValid,
    )
  }

  /**
   * Resets each work item
   */
  @action.bound
  reset() {
    this.itemsToComplete.forEach((ci) => ci.reset())
  }

  /**
   * Completes the current cycle
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    if (!this.completedItems.length) {
      this.newAlert().fromError('There are no items to complete').show()
      return
    }

    try {
      if (this.bookkeepingProgressionInfo?.currentCycle) {
        await this.bookkeepingProgressionStore.completeBookkeepingCycle({
          jobId: this.jobId,
          cycleId: this.bookkeepingProgressionInfo.currentCycle.id,
          completedItems: this.completedItems.map((ci) => ({
            id: ci.workItemId,
            completedCount: ci.completedItems,
          })),
        })
      } else {
        this.newAlert()
          .fromError('There is no cycle currently in progress')
          .show()
      }

      this.reset()
      this.close()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })
}

export class CompletedWorkItemViewModel {
  @observable completedItems = 0

  readonly fieldLengths = {
    completedItems: { min: 0 },
  } as const

  /**
   * Validation context for custom scope change.
   */
  readonly validation = validationContext(this, {
    completedItems: [
      required(),
      () =>
        this.completedItemsAreValid ||
        `The number of completed items must be between ${this.fieldLengths.completedItems.min} and ${this.remaining}.`,
    ],
  })

  constructor(private readonly workItem: WorkItem) {
    makeObservable(this)
  }

  @computed get workItemId() {
    return this.workItem.workItemId
  }

  @computed get remaining() {
    return this.workItem.remaining
  }

  @computed get inventoryItemName() {
    return this.workItem.inventoryItemName
  }

  @computed get inventoryItemDescription() {
    return this.workItem.inventoryItemDescription
  }

  /**
   * Whether the completed items quantity is valid
   */
  @computed
  get completedItemsAreValid() {
    return (
      this.completedItems <= this.remaining &&
      this.completedItems >= this.fieldLengths.completedItems.min
    )
  }

  /**
   * Resets the fields
   */
  @action.bound
  reset() {
    this.completedItems = 1
  }

  /**
   * Sets the number of completed items
   * @param value
   */
  @action.bound
  setCompletedItems(value: number | null) {
    if (!isNil(value)) {
      this.completedItems = value
    }
  }
}
